import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Bug, Camera, Lightbulb, MessageSquarePlus, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Report a problem / suggest something. The page, the user and the browser are captured for you. */
export default function ReportButton() {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("bug");
  const [severity, setSeverity] = useState("annoying");
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const lastError = useRef("");

  useEffect(() => {
    const id = axios.interceptors.response.use(undefined, (e) => {
      const d = e?.response?.data?.detail;
      lastError.current = `${e?.response?.status || "?"} ${e?.config?.method?.toUpperCase() || ""} ${e?.config?.url || ""} — ${typeof d === "string" ? d : e?.message || ""}`.slice(0, 280);
      return Promise.reject(e);
    });
    return () => axios.interceptors.response.eject(id);
  }, []);

  const send = async () => {
    if (text.trim().length < 5) { toast.error("Tell us what happened, in a line or two"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/feedback`, {
        kind, severity: kind === "idea" ? "idea" : severity, text, page: window.location.pathname,
        context: {
          route: window.location.pathname + window.location.search,
          browser: navigator.userAgent, screen: `${window.innerWidth}×${window.innerHeight}`,
          last_error: lastError.current, at_local: new Date().toString(),
        },
      });
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(`${API}/feedback/${r.data.report.id}/shot`, fd).catch(() => toast.warning("The report was sent, the picture did not upload"));
      }
      toast.success(`${r.data.report.ref} sent${severity === "blocks" && kind === "bug" ? " — flagged as stopping your work" : ""}. You can follow it under Help → My reports.`, { duration: 8000 });
      setOpen(false); setText(""); setFile(null); setSeverity("annoying"); setKind("bug");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the report");
    }
    setBusy(false);
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="report-button" title="Report a problem or suggest something"
        className="fixed bottom-20 right-3 md:bottom-5 md:right-5 z-40 h-11 w-11 rounded-full bg-[#1B2A4A] text-white shadow-lg flex items-center justify-center hover:bg-[#E32C17] transition-colors">
        <MessageSquarePlus size={18} />
      </button>
    );
  }

  const inp = "w-full border border-[#E2E5EA] rounded-sm px-2 py-1.5 text-xs";
  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center bg-black/30 p-3" onClick={() => !busy && setOpen(false)}>
      <div className="bg-white rounded-sm border border-[#E2E8F0] shadow-xl w-full max-w-md p-4 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="report-dialog">
        <div className="flex items-center gap-2">
          <div className="text-sm font-bold uppercase tracking-wide">Tell us</div>
          <button onClick={() => setOpen(false)} className="ml-auto text-[#9CA3AF] hover:text-[#1A1D21]" data-testid="report-close"><X size={16} /></button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {[["bug", "Something is wrong", Bug], ["idea", "I have an idea", Lightbulb]].map(([k, l, Icon]) => (
            <button key={k} onClick={() => setKind(k)} data-testid={`report-kind-${k}`}
              className={`flex items-center gap-1.5 px-2 py-2 rounded-sm border text-[11px] font-bold ${kind === k ? "border-[#1B2A4A] bg-[#1B2A4A] text-white" : "border-[#E2E5EA] text-[#4B5563]"}`}>
              <Icon size={13} /> {l}
            </button>
          ))}
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} data-testid="report-text"
          placeholder={kind === "bug" ? "What were you doing, and what happened instead?" : "What would make this easier?"} className={inp} />
        {kind === "bug" && (
          <div className="space-y-1">
            {[["blocks", "It stops my work — I cannot carry on"], ["annoying", "Annoying, but I can work around it"]].map(([v, l]) => (
              <label key={v} className="flex items-center gap-2 text-[11px] cursor-pointer" data-testid={`report-sev-${v}`}>
                <input type="radio" name="report-sev" checked={severity === v} onChange={() => setSeverity(v)} />
                <span className={v === "blocks" ? "font-bold text-[#E32C17]" : ""}>{l}</span>
              </label>
            ))}
          </div>
        )}
        <label className="flex items-center gap-1.5 text-[11px] text-[#4B5563] cursor-pointer" data-testid="report-file-label">
          <Camera size={13} /> {file ? file.name : "Add a photo or screenshot (optional)"}
          <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" data-testid="report-file"
            onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        <div className="text-[10px] text-[#9CA3AF]">We attach the page you are on, your name and your browser — you do not need to describe them.</div>
        <button onClick={send} disabled={busy} className="w-full text-xs font-bold py-2 bg-[#E32C17] text-white rounded-sm disabled:opacity-40" data-testid="report-send">
          {busy ? "Sending…" : "Send it"}
        </button>
      </div>
    </div>
  );
}
